import React, { useEffect, useRef } from "react";

/**
 * ShopifyBuyButton
 * ----------------
 * Renders the merchant-provided Shopify Buy Button collection embed exactly
 * as generated by Shopify (unmodified). Products, pricing, inventory, cart,
 * and checkout are controlled by Shopify — this file adds no synchronization,
 * webhooks, or custom logic.
 *
 * The IIFE script from Shopify expects a specific target <div id="…"> to
 * exist in the DOM before it runs. We render that div in JSX and inject the
 * exact Shopify script into <body> after mount so the IIFE finds its target.
 */

const CONTAINER_ID = "collection-component-1784304696536";

// The following string is the EXACT script body Shopify generated for this
// merchant. Do not edit — it is treated as opaque vendor code.
const SHOPIFY_EMBED_JS = `/*<![CDATA[*/
(function () {
  var scriptURL = 'https://sdks.shopifycdn.com/buy-button/latest/buy-button-storefront.min.js';
  if (window.ShopifyBuy) {
    if (window.ShopifyBuy.UI) {
      ShopifyBuyInit();
    } else {
      loadScript();
    }
  } else {
    loadScript();
  }
  function loadScript() {
    var script = document.createElement('script');
    script.async = true;
    script.src = scriptURL;
    (document.getElementsByTagName('head')[0] || document.getElementsByTagName('body')[0]).appendChild(script);
    script.onload = ShopifyBuyInit;
  }
  function ShopifyBuyInit() {
    var client = ShopifyBuy.buildClient({
      domain: 'hvn653-ee.myshopify.com',
      storefrontAccessToken: 'b327df1ea4faa63e2e12bed8c6f090ed',
    });
    ShopifyBuy.UI.onReady(client).then(function (ui) {
      ui.createComponent('collection', {
        id: '318628200514',
        node: document.getElementById('collection-component-1784304696536'),
        moneyFormat: '%24%7B%7Bamount%7D%7D',
        options: {
  "product": {
    "styles": {
      "product": {
        "@media (min-width: 601px)": {
          "max-width": "calc(25% - 20px)",
          "margin-left": "20px",
          "margin-bottom": "50px",
          "width": "calc(25% - 20px)"
        },
        "img": {
          "height": "calc(100% - 15px)",
          "position": "absolute",
          "left": "0",
          "right": "0",
          "top": "0"
        },
        "imgWrapper": {
          "padding-top": "calc(75% + 15px)",
          "position": "relative",
          "height": "0",
          "cursor": "pointer"
        },
        "title": {
          "cursor": "pointer"
        }
      }
    },
    "text": {
      "button": "Add to cart"
    },
    "events": {
      "afterRender": function (product) {
        try {
          if (!product || !product.node) return;
          var title = product.model && product.model.title;
          if (!title) return;
          var openModal = function (e) {
            if (e) {
              e.preventDefault();
              e.stopPropagation();
            }
            if (typeof product.openModal === "function") {
              product.openModal();
            }
          };
          var cards = product.node.querySelectorAll(".shopify-buy__product");
          for (var i = 0; i < cards.length; i++) {
            var card = cards[i];
            var t = card.querySelector(".shopify-buy__product__title");
            if (!t) continue;
            if ((t.textContent || "").trim() !== title.trim()) continue;
            var imgWrapEl = card.querySelector(".shopify-buy__product-img-wrapper");
            if (!t.getAttribute("data-cu-modal-bound")) {
              t.setAttribute("data-cu-modal-bound", "1");
              t.style.cursor = "pointer";
              t.addEventListener("click", openModal);
            }
            if (imgWrapEl && !imgWrapEl.getAttribute("data-cu-modal-bound")) {
              imgWrapEl.setAttribute("data-cu-modal-bound", "1");
              imgWrapEl.style.cursor = "pointer";
              imgWrapEl.addEventListener("click", openModal);
            }
            break;
          }
        } catch (err) {
          /* swallow to avoid breaking Shopify SDK render */
        }
      }
    }
  },
  "productSet": {
    "styles": {
      "products": {
        "@media (min-width: 601px)": {
          "margin-left": "-20px"
        }
      }
    }
  },
  "modalProduct": {
    "contents": {
      "img": false,
      "imgWithCarousel": true,
      "button": false,
      "buttonWithQuantity": true
    },
    "styles": {
      "product": {
        "@media (min-width: 601px)": {
          "max-width": "100%",
          "margin-left": "0px",
          "margin-bottom": "0px"
        }
      }
    },
    "text": {
      "button": "Add to cart"
    }
  },
  "option": {},
  "cart": {
    "text": {
      "total": "Subtotal",
      "button": "Checkout"
    }
  },
  "toggle": {}
},
      });
    });
  }
})();
/*]]>*/`;

export default function ShopifyBuyButton() {
  const initedRef = useRef(false);

  useEffect(() => {
    // StrictMode double-invokes effects in dev. Guard so we only ever inject
    // and mount the Shopify Buy Button once per component instance. React
    // gives each new mount a fresh ref, so navigating away and back still
    // re-initializes correctly.
    if (initedRef.current) return undefined;
    initedRef.current = true;

    const el = document.createElement("script");
    el.type = "text/javascript";
    el.setAttribute("data-shopify-buy-button", "true");
    el.text = SHOPIFY_EMBED_JS;
    document.body.appendChild(el);

    return () => {
      try {
        document.body.removeChild(el);
      } catch (_) {
        /* already removed */
      }
      const c = document.getElementById(CONTAINER_ID);
      if (c) c.innerHTML = "";
    };
  }, []);

  return <div id={CONTAINER_ID} data-testid="shopify-buy-button-container" />;
}
